import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';

const NotificationCard = ({
  notification,
  isSelected,
  onToggleSelect,
  onMarkAsRead,
  onDelete,
  currentLanguage
}) => {
  const navigate = useNavigate();

  const translations = {
    en: {
      markAsRead: 'Mark as read',
      delete: 'Delete',
      viewDeal: 'View Deal',
      urgent: 'URGENT',
      high: 'HIGH',
      normal: 'NORMAL'
    },
    ko: {
      markAsRead: '읽음으로 표시',
      delete: '삭제',
      viewDeal: '거래 보기',
      urgent: '긴급',
      high: '높음',
      normal: '보통'
    }
  };

  const t = translations?.[currentLanguage];

  const getNotificationIcon = () => {
    switch (notification?.type) {
      case 'payment_update':
        return { name: 'CreditCard', color: 'text-green-600', bgColor: 'bg-green-50' };
      case 'deal_status_change':
        return { name: 'Tag', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case 'dispute_filed':
        return { name: 'AlertTriangle', color: 'text-red-600', bgColor: 'bg-red-50' };
      case 'system_alert':
        if (notification?.metadata?.flightNumber || notification?.title?.toLowerCase()?.includes('flight')) {
          return { name: 'Plane', color: 'text-orange-600', bgColor: 'bg-orange-50' };
        }
        return { name: 'Shield', color: 'text-purple-600', bgColor: 'bg-purple-50' };
      case 'new_message':
        return { name: 'MessageSquare', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case 'delivery_confirmation':
        return { name: 'Package', color: 'text-green-600', bgColor: 'bg-green-50' };
      default:
        return { name: 'Bell', color: 'text-gray-600', bgColor: 'bg-gray-50' };
    }
  };

  const getPriorityBadge = () => {
    const priority = notification?.metadata?.priority;
    if (!priority) return null;

    const priorityConfig = {
      urgent: { label: t?.urgent, color: 'bg-red-600 text-white' },
      high: { label: t?.high, color: 'bg-orange-600 text-white' },
      normal: { label: t?.normal, color: 'bg-gray-400 text-white' }
    };

    const config = priorityConfig?.[priority];
    if (!config) return null;

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: currentLanguage === 'ko' ? ko : undefined
      });
    } catch (error) {
      return timestamp;
    }
  };

  const handleCardClick = () => {
    if (!notification?.isRead) {
      onMarkAsRead(notification?.id);
    }

    // Navigate to relevant page if dealId exists
    if (notification?.dealId) {
      navigate(`/deal-room-page?dealId=${notification?.dealId}`);
    }
  };

  const iconConfig = getNotificationIcon();

  return (
    <div
      className={`relative flex items-start gap-4 p-4 rounded-lg border transition-all ${
        notification?.isRead
          ? 'bg-card border-border' :'bg-accent/20 border-accent shadow-sm'
      } ${
        isSelected ? 'ring-2 ring-primary' : ''
      } hover:shadow-md cursor-pointer`}
      onClick={handleCardClick}
    >
      {/* Selection Checkbox */}
      <div className="flex-shrink-0 pt-1">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e?.stopPropagation();
            onToggleSelect(notification?.id);
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      </div>
      {/* Icon */}
      <div className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconConfig?.bgColor}`}>
          <Icon name={iconConfig?.name} size={24} className={iconConfig?.color} />
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`font-semibold ${
              notification?.isRead ? 'text-foreground' : 'text-foreground font-bold'
            }`}>
              {notification?.title}
            </h4>
            {getPriorityBadge()}
            {!notification?.isRead && (
              <span className="w-2 h-2 bg-primary rounded-full"></span>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatTimestamp(notification?.createdAt)}
          </span>
        </div>

        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
          {notification?.message}
        </p>

        {/* Metadata Display */}
        {notification?.metadata && Object.keys(notification?.metadata)?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {notification?.metadata?.flightNumber && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                <Icon name="Plane" size={12} />
                {notification?.metadata?.flightNumber}
              </span>
            )}
            {notification?.metadata?.amount && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                <Icon name="DollarSign" size={12} />
                ${notification?.metadata?.amount}
              </span>
            )}
            {notification?.metadata?.oldStatus && notification?.metadata?.newStatus && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                {notification?.metadata?.oldStatus} → {notification?.metadata?.newStatus}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-2">
          {notification?.dealId && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                navigate(`/deal-room-page?dealId=${notification?.dealId}`);
              }}
              className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {t?.viewDeal} →
            </button>
          )}
          {!notification?.isRead && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onMarkAsRead(notification?.id);
              }}
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t?.markAsRead}
            </button>
          )}
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onDelete(notification?.id);
            }}
            className="ml-auto text-xs font-medium text-destructive hover:text-destructive/80 transition-colors"
          >
            {t?.delete}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;